import { ReactNode, useEffect, useMemo, useState } from "react";
import {
  Alert,
  AlertDescription,
  AlertIcon,
  Badge,
  Box,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  Flex,
  Menu,
  MenuDivider,
  MenuItem,
  MenuList,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
} from "@chakra-ui/react";
import { CopyIcon, EditIcon } from "@chakra-ui/icons";
import { gql } from "__generated__/gql";
import { useQuery } from "utils/apollo/hooks";
import { ADMIN_CONTEXT } from "utils/apollo/hooks/admin";
import { useActiveBrandIdContext } from "utils/apollo/hooks/brand";
import { formatInteger } from "utils/formatting";
import MoreMenuButton from "components/Shared/MoreMenuButton";
import { FREESTANDING_TABLE_CONTAINER_PROPS } from "components/Shared/ChakraReactTable/FullPageTable";
import { assertPresence } from "utils/assertions";
import { asyncVoid } from "utils/asyncVoidHandler";
import { APP_NAME } from "utils/config";
import { ADMIN_DISTRIBUTION_CENTERS_INDEX } from "utils/links";
import LinkButton from "components/Shared/LinkButton";
import CreateButton from "components/Shared/CreateButton";
import AdminOrderingPlatformBrandsTable from "components/Admin/OrderingPlatforms/BrandsTable";
import { DistributionCenterFilter } from "__generated__/gql/graphql";
import MergeModalMenuItem from "components/Shared/MergeModal/MergeModalMenuItem";
import IsCustomBadge, { getIsOrderingPlatformCustom } from "./IsCustomBadge";
import DistributionCentersDirectoryTable, {
  DISTRIBUTION_CENTER_COLUMNS,
} from "./DistributionCenters/Table/Table";
import OrderingPlatformDetails from "./Details";
import EditOrderingPlatformDetails from "./Edit";
import DeleteMenuItem from "./DeleteMenuItem";
import CreateDistributionCenter from "./DistributionCenters/Selected/Create";
import MergeOrderingPlatform from "./MergeOrderingPlatform";
import OrderingPlatformMakeDefaultMenuItem from "./MakeDefaultMenuItem";

const query = gql(/* GraphQL */ `
  query OrderingPlatformDirectorySelected(
    $id: UUID!
    $isAdmin: Boolean = false
  ) {
    orderingPlatform(id: $id) {
      id
      name
      forBrand @include(if: $isAdmin) {
        id
        name
      }
      distributionCenters(filter: { name: { isNull: false } }) {
        totalCount
      }
      brandOrderingPlatforms {
        totalCount
      }
      ...OrderingPlatformDetails
      ...CustomerIsCustomBadge
      ...EditOrderingPlatformDetails
      ...OrderingPlatformMakeDefaultMenuItemFragment
      ...OrderingPlatformDeleteMenuItemFragment
    }
  }
`);

type Props = {
  footer?: ReactNode;
  isAdmin?: boolean;
  isReadOnly?: boolean;
  onClose: () => void;
  onSelectedOrderingPlatformIdChange?: (id: UUID) => void;
  selectedOrderingPlatformId: UUID | null;
};

export default function OrderingPlatformDrawerContent({
  footer,
  isAdmin = false,
  isReadOnly = false,
  onClose,
  onSelectedOrderingPlatformIdChange,
  selectedOrderingPlatformId,
}: Props) {
  const [isEditing, setIsEditing] = useState(false);
  const [isAddingDistributionCenter, setIsAddingDistributionCenter] =
    useState(false);

  useEffect(() => {
    setIsEditing(false);
  }, [selectedOrderingPlatformId]);

  const brandContext = useActiveBrandIdContext();

  const { data, loading } = useQuery(query, {
    context: isAdmin ? ADMIN_CONTEXT : brandContext,
    skip: !selectedOrderingPlatformId,
    variables: selectedOrderingPlatformId
      ? { id: selectedOrderingPlatformId, isAdmin }
      : undefined,
    returnPartialData: true,
    fetchPolicy: "cache-and-network",
  });

  const isCustom =
    data?.orderingPlatform &&
    getIsOrderingPlatformCustom(data.orderingPlatform);
  const isEditable = !!data?.orderingPlatform && isCustom !== isAdmin;
  const numDcs = data?.orderingPlatform?.distributionCenters?.totalCount;
  const numBrands = data?.orderingPlatform?.brandOrderingPlatforms?.totalCount;

  const dcTableFilter = useMemo<DistributionCenterFilter>(() => {
    const filter: DistributionCenterFilter = { name: { isNull: false } };
    if (selectedOrderingPlatformId) {
      filter.orderingPlatformId = { equalTo: selectedOrderingPlatformId };
    }
    return filter;
  }, [selectedOrderingPlatformId]);

  return (
    <DrawerContent bg="cardBackground">
      <DrawerHeader fontWeight="inherit">
        <Flex alignItems="center" gap={3}>
          <Box fontWeight="semibold">{data?.orderingPlatform?.name}</Box>

          {data?.orderingPlatform && (
            <IsCustomBadge
              orderingPlatform={data.orderingPlatform}
              fontWeight="semibold"
            />
          )}

          <Flex ml="auto" gap={2} alignSelf="flex-start">
            {((!isReadOnly && isEditable) || isAdmin) && (
              <Menu>
                <MoreMenuButton
                  aria-label="Customer Actions"
                  size="md"
                  px=".2em"
                  py=".2em"
                  isDisabled={isEditing}
                />
                <MenuList fontSize="md">
                  <MenuItem
                    icon={<EditIcon />}
                    onClick={() => setIsEditing(true)}
                    isDisabled={!isEditable}
                  >
                    Edit Customer Details…
                  </MenuItem>

                  {isAdmin && (
                    <MenuItem
                      icon={<CopyIcon />}
                      isDisabled={!selectedOrderingPlatformId}
                      onClick={(e) => {
                        e.stopPropagation();
                        assertPresence(
                          selectedOrderingPlatformId,
                          "Expected customer ID to be present",
                        );
                        asyncVoid(
                          navigator.clipboard.writeText(
                            selectedOrderingPlatformId,
                          ),
                        );
                      }}
                    >
                      Copy Customer ID
                    </MenuItem>
                  )}
                  <MenuDivider />

                  {isAdmin && data?.orderingPlatform?.forBrand && (
                    <OrderingPlatformMakeDefaultMenuItem
                      orderingPlatform={data.orderingPlatform}
                    />
                  )}

                  <MergeModalMenuItem
                    label={`Merge ${isCustom ? "Custom" : "Default"} Customer`}
                    isDisabled={
                      !data?.orderingPlatform || !(isAdmin || isCustom)
                    }
                    size="4xl"
                    renderContent={(props) => {
                      if (!selectedOrderingPlatformId) return <>ERROR</>;
                      return (
                        <MergeOrderingPlatform
                          {...props}
                          isAdmin={isAdmin}
                          orderingPlatformId={selectedOrderingPlatformId}
                          onCompleted={() => onClose()}
                        />
                      );
                    }}
                  />

                  <MenuDivider />

                  <DeleteMenuItem
                    isAdmin={isAdmin}
                    isDisabled={!isEditable}
                    orderingPlatform={data?.orderingPlatform}
                    onCompleted={onClose}
                  />
                </MenuList>
              </Menu>
            )}
            <DrawerCloseButton position="static" isDisabled={isEditing} />
          </Flex>
        </Flex>

        {isAdmin && data?.orderingPlatform?.forBrand && (
          <Alert fontSize="sm" mt={3} p={2}>
            <AlertIcon />
            <AlertDescription lineHeight="normal">
              This is a custom customer for the brand{" "}
              <strong>{data.orderingPlatform.forBrand.name}</strong>. To edit
              this customer, please go to the brand’s {APP_NAME} instance.
            </AlertDescription>
          </Alert>
        )}
      </DrawerHeader>

      {isEditing ? (
        <EditOrderingPlatformDetails
          data={data?.orderingPlatform}
          isAdmin={isAdmin}
          isLoading={loading}
          onCloseEdit={() => setIsEditing(false)}
        />
      ) : (
        <>
          <DrawerBody display="flex" flexFlow="column" flex="1 1 100%">
            <Tabs
              isLazy
              size="sm"
              colorScheme="lightPinkButton"
              display="flex"
              flexFlow="column"
              flex="1 1 100%"
              minH={0}
            >
              <TabList>
                <Tab>Details</Tab>

                <Tab>
                  Distribution Centers
                  {!!numDcs && numDcs > 0 && (
                    <Badge
                      colorScheme="pinkButton"
                      variant="outline"
                      ml={2}
                      lineHeight={1.5}
                    >
                      {formatInteger(numDcs)}
                    </Badge>
                  )}
                </Tab>

                {isAdmin && (
                  <Tab>
                    Brands
                    {!!numBrands && numBrands > 0 && (
                      <Badge
                        colorScheme="pinkButton"
                        variant="outline"
                        ml={2}
                        lineHeight={1.5}
                      >
                        {formatInteger(numBrands)}
                      </Badge>
                    )}
                  </Tab>
                )}
              </TabList>

              <TabPanels
                display="flex"
                flexFlow="column"
                flex="1 1 100%"
                minH={0}
              >
                <TabPanel px={0}>
                  <OrderingPlatformDetails
                    {...{ isAdmin, onSelectedOrderingPlatformIdChange }}
                    data={data?.orderingPlatform}
                    isLoading={loading}
                  />
                </TabPanel>

                <TabPanel
                  px={0}
                  display="flex"
                  flexFlow="column"
                  flex="1 1 100%"
                  minH="25rem"
                >
                  <Flex justifyContent="flex-end" gap={2} pb={3}>
                    {!isReadOnly && (isEditable || isAdmin) && (
                      <CreateButton
                        onClick={() => setIsAddingDistributionCenter(true)}
                        isDisabled={!isEditable}
                      >
                        Add Distribution Center
                      </CreateButton>
                    )}

                    {isAdmin && (
                      <LinkButton
                        href={{
                          pathname: ADMIN_DISTRIBUTION_CENTERS_INDEX,
                          // eslint-disable-next-line @typescript-eslint/naming-convention
                          query: {
                            orderingPlatform: selectedOrderingPlatformId,
                            isDefault: false,
                          },
                        }}
                        size="xs"
                        colorScheme="pinkButton"
                        variant="outline"
                      >
                        View in Distribution Centers ►
                      </LinkButton>
                    )}
                  </Flex>

                  <DistributionCentersDirectoryTable
                    isAdmin={isAdmin}
                    columns={DISTRIBUTION_CENTER_COLUMNS}
                    filter={dcTableFilter}
                    isReady={!!selectedOrderingPlatformId}
                    defaultPageSize={25}
                    {...FREESTANDING_TABLE_CONTAINER_PROPS}
                    scrollContainerProps={{
                      ...FREESTANDING_TABLE_CONTAINER_PROPS.scrollContainerProps,
                      maxHeight: undefined,
                      minHeight: undefined,
                      bg: "gray.50",
                    }}
                  />
                </TabPanel>

                {isAdmin && (
                  <TabPanel
                    px={0}
                    display="flex"
                    flexFlow="column"
                    flex="1 1 100%"
                    minH="25rem"
                  >
                    <AdminOrderingPlatformBrandsTable
                      orderingPlatformId={selectedOrderingPlatformId}
                    />
                  </TabPanel>
                )}
              </TabPanels>
            </Tabs>
          </DrawerBody>

          {footer && <DrawerFooter>{footer}</DrawerFooter>}

          {isEditable && (
            <Drawer
              size="md"
              isOpen={isAddingDistributionCenter}
              onClose={() => setIsAddingDistributionCenter(false)}
            >
              <CreateDistributionCenter
                isAdmin={isAdmin}
                onCancel={() => setIsAddingDistributionCenter(false)}
                onCompleted={() => setIsAddingDistributionCenter(false)}
                orderingPlatformId={selectedOrderingPlatformId ?? undefined}
              />
            </Drawer>
          )}
        </>
      )}
    </DrawerContent>
  );
}
